import { useCallback, useRef, useState } from "react";

import { ImageIcon, Loader2, Upload, X } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { toast } from "@/shared/lib/toast";
import { cn } from "@/shared/lib/utils";
import { useCreateUploadMutation } from "@/store/apis/adminApi.enhanced";
import { Content_type, Purpose } from "@/store/apis/adminApi.generated";

interface ImageUploadProps {
  /** URL pública actual (string vacío = sin imagen). Controlado. */
  value: string;
  onChange: (next: string) => void;
  /** `purpose` del upload. */
  purpose?: Purpose;
  /** Máximo peso en bytes. Default 2 MB. */
  maxBytes?: number;
  className?: string;
}

const DEFAULT_MAX = 2 * 1024 * 1024; // 2 MB
const ACCEPTED: Record<Content_type, string> = {
  [Content_type.ImagePng]: "PNG",
  [Content_type.ImageJpeg]: "JPG",
  [Content_type.ImageWebp]: "WebP",
};

/**
 * Upload de imagen contra el flow de signed URL del back.
 *
 * Flow:
 *   1. Usuaria elige archivo (drag or click).
 *   2. Validación cliente: content_type + size.
 *   3. `POST /uploads` con `content_type` → back devuelve `signed_url`
 *      y `public_url`.
 *   4. `PUT` directo al `signed_url` con el blob del archivo.
 *   5. Guardamos `public_url` en el estado del form.
 *
 * Blindaje:
 *   - Si el PUT falla, no commiteamos el `onChange` — el valor del form
 *     queda intacto.
 *   - Loading state cubre los dos pasos (createUpload + PUT).
 *   - El input `<input type="file">` queda oculto; el drop-zone y botón
 *     son los triggers accesibles.
 */
export function ImageUpload({
  value,
  onChange,
  purpose = Purpose.Popup,
  maxBytes = DEFAULT_MAX,
  className,
}: ImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [createUpload] = useCreateUploadMutation();

  const upload = useCallback(
    async (file: File) => {
      const type = file.type as Content_type;
      if (!(Object.values(Content_type) as string[]).includes(type)) {
        toast.error(`Formato no permitido. Usa ${Object.values(ACCEPTED).join(", ")}.`);
        return;
      }
      if (file.size > maxBytes) {
        toast.error(`Imagen muy pesada. Máximo ${Math.round(maxBytes / 1024 / 1024)} MB.`);
        return;
      }

      setUploading(true);
      try {
        const ticket = await createUpload({
          body: { purpose, content_type: type },
        }).unwrap();

        const putRes = await fetch(ticket.signed_url, {
          method: "PUT",
          headers: { "content-type": type },
          body: file,
        });
        if (!putRes.ok) throw new Error(`Upload falló (${putRes.status})`);

        onChange(ticket.public_url);
        toast.success("Imagen subida");
      } catch (err) {
        toast.error(err);
      } finally {
        setUploading(false);
      }
    },
    [createUpload, maxBytes, onChange, purpose],
  );

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void upload(file);
  };

  // ─── Preview ──────────────────────────────────────────────
  if (value) {
    return (
      <div className={cn("space-y-2", className)}>
        <div className="group relative overflow-hidden rounded-xl border bg-muted/40">
          <img src={value} alt="Vista previa del pop-up" className="h-40 w-full object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            disabled={uploading}
            aria-label="Quitar imagen"
            className={cn(
              "absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-md",
              "bg-background/90 text-foreground shadow backdrop-blur transition-opacity",
              "hover:bg-background",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full"
        >
          {uploading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Subiendo…
            </>
          ) : (
            <>
              <Upload className="h-3.5 w-3.5" />
              Reemplazar imagen
            </>
          )}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={Object.keys(ACCEPTED).join(",")}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void upload(file);
            e.target.value = ""; // permite re-seleccionar el mismo archivo.
          }}
        />
      </div>
    );
  }

  // ─── Dropzone ─────────────────────────────────────────────
  return (
    <div className={className}>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          "flex cursor-pointer flex-col items-center justify-center gap-2.5 rounded-xl border border-dashed bg-muted/30 px-4 py-8 text-center",
          "transition-colors",
          dragging ? "border-primary bg-primary/5" : "hover:bg-muted/50",
          uploading && "pointer-events-none opacity-70",
        )}
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ImageIcon className="h-4 w-4" />
          )}
        </div>
        <div className="space-y-0.5">
          <p className="text-[13px] font-medium text-foreground">
            {uploading
              ? "Subiendo imagen…"
              : dragging
                ? "Suelta la imagen aquí"
                : "Arrastra una imagen o haz clic"}
          </p>
          <p className="font-mono text-[11px] text-muted-foreground">
            {Object.values(ACCEPTED).join(" · ")} · máx {Math.round(maxBytes / 1024 / 1024)} MB
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={Object.keys(ACCEPTED).join(",")}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void upload(file);
            e.target.value = "";
          }}
        />
      </label>
    </div>
  );
}
